import { useCallback, useRef } from 'react';

import { useChatActions } from '@/features/ai-chat/state/useChat';
import { useAddToDashboardAction } from '@/features/markets';
import type { BaseTabType } from '@/features/markets';

/**
 * Minimal, library-agnostic projection of a TanStack `ToolCallPart` in
 * `state: 'approval-requested'` that this prompt needs to resolve the
 * approval and report the tool's result.
 *
 * `toolCallId`/`toolName` map to `ToolCallPart.id`/`.name`; `approvalId` maps
 * to `ToolCallPart.approval.id` — a distinct id from `toolCallId` (see
 * `@tanstack/ai-client`'s `ChatClient.addToolApprovalResponse`, which looks up
 * the tool call by matching `approval.id`, not `id`). Task 11 builds this from
 * the real `ToolCallPart` on `ChatState.messages`.
 */
export interface ToolApprovalInterrupt {
  toolCallId: string;
  toolName: string;
  approvalId: string;
}

/**
 * Drives the add_to_dashboard approval prompt.
 *
 * There is no top-level `interrupts` array on `useChat()` in the installed
 * `@tanstack/ai-react@0.18.1` — approval state lives on the message's
 * `ToolCallPart.approval` field, and it is resolved via `useChat()`'s
 * `addToolApprovalResponse({ id, approved })`, not a per-interrupt
 * `resolveInterrupt` callback. See task-9-report.md for source citations.
 */
export function useToolApprovalPrompt(
  interrupt: ToolApprovalInterrupt,
  tab: BaseTabType,
  onResolved?: (approved: boolean) => void
) {
  const approveButtonRef = useRef<HTMLButtonElement>(null);
  const { addToolApprovalResponse, addToolResult } = useChatActions();

  const action = useAddToDashboardAction(tab, {
    onAdded: () => {
      void addToolResult({
        toolCallId: interrupt.toolCallId,
        tool: interrupt.toolName,
        output: { added: true }
      });
    }
  });

  const respond = useCallback(
    (approved: boolean) => {
      void addToolApprovalResponse({ id: interrupt.approvalId, approved });
      onResolved?.(approved);
    },
    [addToolApprovalResponse, interrupt.approvalId, onResolved]
  );

  const handleApprove = useCallback(() => {
    respond(true);
    if (approveButtonRef.current) action.openFrom(approveButtonRef.current);
  }, [respond, action]);

  const handleDeny = useCallback(() => {
    respond(false);
  }, [respond]);

  return {
    approveButtonRef,
    disabled: action.disabled,
    layer: action.layer,
    handleApprove,
    handleDeny
  };
}
